import { useContext, useEffect, useState } from 'react';
import { useQboApi } from '../../clients/quipli-service/qbo/qbo-api-v1';
import { QBOPurchaseOrder } from '../../clients/quipli-service/qbo/qbo';
import { displayMoneyFormat } from '../../util/util';
import { TenantContext } from '../../context/tenant-context';
import { Button } from '../../components/form/button';
import { toast } from '../../util/toasts';
import { OrderSection } from '../order/order-section/order-section';
import { Typography } from '../../components/typography/typography';
import { DataTable } from '../../components/table/data-table';
import classNames from 'classnames';
import { isMobileOnly } from 'react-device-detect';
import { PurchaseOrder } from '../../clients/quipli-service/purchase-order/purchase-order';

export const QboPurchaseOrder = ({ purchaseOrder }: { purchaseOrder: PurchaseOrder }) => {
  const { contextPath, configuration } = useContext(TenantContext);
  const [isSyncingInvoice, setIsSyncingInvoice] = useState<boolean>(false);
  const [qboPurchaseOrder, setQboPurchaseOrder] = useState<QBOPurchaseOrder | undefined>();
  const { syncInvoice, getPurchaseOrder } = useQboApi();
  let qboEnabled = false;

  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }

  useEffect(() => {
    if (qboEnabled) {
      getPurchaseOrder(purchaseOrder.id)
        .then((i) => {
          setQboPurchaseOrder(i);
        })
        .catch((e) => {
          console.error(`Error loading QuickBooks purchase order`, e);
          toast(`Error loading QuickBooks purchase order`, 'error');
        });
    }
  }, [configuration?.qboEnabled]);
  // @hipaa-exclude-end

  async function triggerInvoiceSync() {
    setIsSyncingInvoice(true);
    try {
      await syncInvoice({ invoiceId: purchaseOrder.invoices.find(() => true)!.id });
      await getPurchaseOrder(purchaseOrder.id).then(setQboPurchaseOrder);
      toast('Purchase Order synced successfully');
    } catch (ex: any) {
      toast(ex.message, 'error');
    }
    setIsSyncingInvoice(false);
  }

  return (
    <>
      {qboEnabled && (
        <OrderSection
          header={
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                QBO Purchase Orders
              </Typography>
              <Button
                className="button button-secondary"
                disabled={purchaseOrder.invoices.length === 0 || isSyncingInvoice}
                onClick={triggerInvoiceSync}
              >
                Sync Purchase Order
              </Button>
            </div>
          }
          className="flex flex-col gap-[8px]"
        >
          <div
            className={classNames({
              'border border-solid border-grey-200 rounded-[9px]': !isMobileOnly,
            })}
          >
            <DataTable
              columnNames={[
                { key: 'date', label: 'Date' },
                { key: 'invoiceId', label: 'Purchase Order ID', showRule: 'OnlyDesktop' },
                { key: 'amount', label: 'Amount' },
                { key: 'download', label: '' },
              ]}
              data={
                qboPurchaseOrder
                  ? [
                      {
                        mobileHeader: (
                          <>
                            <Typography type="text-md" weight="medium">
                              Invoice ID
                            </Typography>
                            <Typography type="text-sm" weight="regular" className="text-grey-500">
                              {`purchaseorder_${qboPurchaseOrder.Id}`}
                            </Typography>
                          </>
                        ),
                        fields: {
                          date: qboPurchaseOrder.TxnDate,
                          invoiceId: `purchaseorder_${qboPurchaseOrder.Id}`,
                          amount: (
                            <>
                              <Typography type="text-sm" className="text-quipli-black-800">
                                {displayMoneyFormat(qboPurchaseOrder.TotalAmt, configuration?.currency)}
                              </Typography>
                            </>
                          ),
                          download: (
                            <Button
                              className={classNames('button-primary', {
                                'w-full': isMobileOnly,
                              })}
                            >
                              <a
                                target="_blank"
                                href={`${
                                  contextPath ? contextPath : '/'
                                }quipli-service/v1/qbo/getPurchaseOrderAsPdf?qboId=${qboPurchaseOrder.Id}`}
                                title="Download Invoice"
                              >
                                <Typography type="text-sm" className="text-quipli-white">
                                  Download
                                </Typography>
                              </a>
                            </Button>
                          ),
                        },
                      },
                    ]
                  : []
              }
            />
          </div>
        </OrderSection>
      )}
    </>
  );
};
